import { useContext } from "react";
import AuthContext from "../AuthContext";
import { privateAxios } from "../utils/axios";

// Import the permission context conditionally at the module level
let PermissionContext = null;
let usePermissionContext = null;

try {
  const permissionModule = require("../contexts/PermissionContext");
  usePermissionContext = permissionModule.usePermissionContext;
} catch (error) {
  // Context not available, continue without it
}

const usePermissions = () => {
  const auth = useContext(AuthContext);

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  };

  // Refresh user permissions from server
  const refreshPermissions = async () => {
    try {
      const response = await privateAxios.get("/auth/me");
      if (response.data.success) {
        // Update localStorage with fresh user data
        localStorage.setItem("user", JSON.stringify(response.data.data));
        return response.data.data;
      }
    } catch (error) {
      console.error("Error refreshing permissions:", error);
      throw error;
    }
  };

  // Check if user has specific permission for a module
  const hasPermission = (moduleName, permissionType = "read") => {
    if (!auth.user) return false;

    const userData = getUserData();
    if (
      !userData ||
      !userData.permissions ||
      !Array.isArray(userData.permissions)
    ) {
      return false;
    }

    // Find the module in user permissions
    const modulePermission = userData.permissions.find(
      (perm) => perm.module === moduleName
    );

    // Check if the module exists and has the required permission
    if (modulePermission && modulePermission.permissions) {
      return modulePermission.permissions.includes(permissionType);
    }

    return false;
  };

  // Check if user has read permission
  const canRead = (moduleName) => hasPermission(moduleName, "read");

  // Check if user has write permission
  const canWrite = (moduleName) => hasPermission(moduleName, "write");

  // Check if user has delete permission
  const canDelete = (moduleName) => hasPermission(moduleName, "delete");

  // Check if user has any permission for a module
  const hasAnyPermission = (moduleName) => {
    if (!auth.user) return false;

    const userData = getUserData();
    if (
      !userData ||
      !userData.permissions ||
      !Array.isArray(userData.permissions)
    ) {
      return false;
    }

    const modulePermission = userData.permissions.find(
      (perm) => perm.module === moduleName
    );

    return (
      modulePermission &&
      modulePermission.permissions &&
      modulePermission.permissions.length > 0
    );
  };

  // Get all permissions for a module
  const getModulePermissions = (moduleName) => {
    if (!auth.user) return [];

    const userData = getUserData();
    if (
      !userData ||
      !userData.permissions ||
      !Array.isArray(userData.permissions)
    ) {
      return [];
    }

    const modulePermission = userData.permissions.find(
      (perm) => perm.module === moduleName
    );

    return modulePermission ? modulePermission.permissions : [];
  };

  // Check if user is admin
  const isAdmin = () => {
    const userData = getUserData();
    return userData && userData.role === "admin";
  };

  // Get user role
  const getUserRole = () => {
    const userData = getUserData();
    return userData ? userData.role : null;
  };

  return {
    hasPermission,
    canRead,
    canWrite,
    canDelete,
    hasAnyPermission,
    getModulePermissions,
    isAdmin,
    getUserRole,
    refreshPermissions,
    userData: getUserData(),
  };
};

export default usePermissions;
